import React, { useState, useEffect, useCallback } from 'react';
import { Plus, BarChart2, Users, CheckCircle, Clock, Trash2, UserCheck, Edit2, X, Save, Calendar, MapPin, Lock, Unlock } from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import API_URL from '../api';
import RichTextEditor from '../components/RichTextEditor';
import StagesEditor from '../components/StagesEditor';

// Format date as DD/MM/YYYY
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false
  });
};

// ✅ Convert datetime-local string to UTC ISO
const toUTC = (localStr) => {
  if (!localStr) return '';
  return new Date(localStr).toISOString();
};

// ✅ Convert UTC from MongoDB back to local datetime-local format
const toLocal = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
};

function Countdown({ deadline }) {
  const [timeLeft, setTimeLeft] = useState('');
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const calc = () => {
      const diff = new Date(deadline) - new Date();
      if (diff <= 0) { setExpired(true); setTimeLeft('Expired'); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(d > 0 ? `${d}d ${h}h ${m}m` : h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`);
    };
    calc();
    const interval = setInterval(calc, 1000);
    return () => clearInterval(interval);
  }, [deadline]);

  return (
    <span style={{ background: expired ? 'rgba(255,68,68,0.1)' : 'rgba(255,152,0,0.1)', color: expired ? '#ff4444' : '#ff9800', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>
      ⏰ {expired ? 'Deadline Passed' : `Closes in ${timeLeft}`}
    </span>
  );
}

function EventForm({ form, onChange, darkMode, isEdit }) {
  const text = darkMode ? '#e0e0e0' : '#333';
  const subtext = darkMode ? '#888' : '#666';
  const inputBg = darkMode ? '#0f0f1a' : '#fff';
  const inputBorder = darkMode ? 'rgba(255,255,255,0.1)' : '#e0e0e0';

  const inputStyle = {
    background: inputBg, border: `1.5px solid ${inputBorder}`, color: text,
    padding: '12px', borderRadius: '10px', fontSize: '14px', width: '100%',
    marginBottom: 0, fontFamily: 'inherit', outline: 'none'
  };

  const label = (txt) => (
    <label style={{ color: subtext, fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '8px' }}>{txt}</label>
  );

  const field = (content) => (
    <div style={{ marginBottom: '14px' }}>{content}</div>
  );

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
        <div>
          {label('Event Title')}
          <input placeholder="e.g. Tech Fest 2025" value={form.title} onChange={e => onChange({ ...form, title: e.target.value })} style={inputStyle} />
        </div>
        <div>
          {label('Venue')}
          <input placeholder="e.g. Main Auditorium" value={form.venue} onChange={e => onChange({ ...form, venue: e.target.value })} style={inputStyle} />
        </div>
      </div>

      {field(<>
        {label('Short Description')}
        <input placeholder="Brief summary of the event" value={form.description} onChange={e => onChange({ ...form, description: e.target.value })} style={inputStyle} />
      </>)}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
        <div>
          {label('Event Date')}
          <input type="date" value={form.date} onChange={e => onChange({ ...form, date: e.target.value })} style={{ ...inputStyle, colorScheme: darkMode ? 'dark' : 'light' }} />
          {form.date && <p style={{ color: '#e94560', fontSize: '12px', marginTop: '5px', fontWeight: '600' }}>📅 {formatDate(form.date)}</p>}
        </div>
        <div>
          {label('Total Seats')}
          <input placeholder="e.g. 100" type="number" value={form.totalSeats} onChange={e => onChange({ ...form, totalSeats: e.target.value })} style={inputStyle} />
        </div>
      </div>

      {field(<>
        {label('Rules & Guidelines (optional)')}
        <textarea placeholder="List any rules or requirements..." value={form.rules} onChange={e => onChange({ ...form, rules: e.target.value })} style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} />
      </>)}

      {field(<>
        <label style={{ color: subtext, fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '8px' }}>
          Detailed Info <span style={{ fontWeight: '400', textTransform: 'none' }}>(optional)</span>
        </label>
        <RichTextEditor value={form.details} onChange={val => onChange({ ...form, details: val })} />
      </>)}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
        <div>
          {label('🟢 Registration Opens (optional)')}
          <input
            type="datetime-local"
            value={form.registrationStartDate || ''}
            onChange={e => onChange({ ...form, registrationStartDate: e.target.value })}
            style={{ ...inputStyle, colorScheme: darkMode ? 'dark' : 'light' }}
          />
          {form.registrationStartDate && (
            <p style={{ color: '#4caf50', fontSize: '12px', marginTop: '5px', fontWeight: '600' }}>
              🟢 {formatDateTime(form.registrationStartDate)}
            </p>
          )}
        </div>
        <div>
          {label('🔴 Registration Closes (optional)')}
          <input
            type="datetime-local"
            value={form.registrationDeadline || ''}
            onChange={e => onChange({ ...form, registrationDeadline: e.target.value })}
            style={{ ...inputStyle, colorScheme: darkMode ? 'dark' : 'light' }}
          />
          {form.registrationDeadline && (
            <div style={{ marginTop: '5px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ color: '#e94560', fontSize: '12px', fontWeight: '600' }}>
                🔴 {formatDateTime(form.registrationDeadline)}
              </span>
              <Countdown deadline={new Date(form.registrationDeadline).toISOString()} />
            </div>
          )}
        </div>
      </div>

      <StagesEditor stages={form.stages || []} onChange={val => onChange({ ...form, stages: val })} />

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
        <input type="checkbox" id={`cancel-${isEdit ? 'edit' : 'new'}`} checked={form.allowCancellation} onChange={e => onChange({ ...form, allowCancellation: e.target.checked })} style={{ width: 'auto', accentColor: '#e94560' }} />
        <label htmlFor={`cancel-${isEdit ? 'edit' : 'new'}`} style={{ color: text, fontSize: '14px' }}>Allow Cancellation</label>
      </div>
    </>
  );
}

function AdminDashboard() {
  const { darkMode } = useTheme();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [registrations, setRegistrations] = useState([]);
  const [newEvent, setNewEvent] = useState({ title: '', description: '', date: '', venue: '', totalSeats: '', allowCancellation: true, rules: '', details: '', registrationDeadline: '', registrationStartDate: '', stages: [] });
  const [scanEmail, setScanEmail] = useState('');
  const [scanMsg, setScanMsg] = useState('');
  const [editingEvent, setEditingEvent] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [activeTab, setActiveTab] = useState('create');

  const bg = darkMode ? '#0f0f1a' : '#f0f2f5';
  const card = darkMode ? '#1a1a2e' : '#fff';
  const border = darkMode ? 'rgba(255,255,255,0.07)' : '#f0f0f0';
  const text = darkMode ? '#e0e0e0' : '#333';
  const subtext = darkMode ? '#888' : '#666';
  const inputBg = darkMode ? '#0f0f1a' : '#fff';
  const inputBorder = darkMode ? 'rgba(255,255,255,0.1)' : '#e0e0e0';

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = () => {
    axios.get(`${API_URL}/api/events`).then(res => setEvents(res.data));
  };

  const createEvent = async () => {
    try {
      // ✅ Convert local times to UTC before saving
      const payload = {
        ...newEvent,
        registrationDeadline: toUTC(newEvent.registrationDeadline),
        registrationStartDate: toUTC(newEvent.registrationStartDate),
      };
      await axios.post(`${API_URL}/api/events`, payload);
      setNewEvent({ title: '', description: '', date: '', venue: '', totalSeats: '', allowCancellation: true, rules: '', details: '', registrationDeadline: '', registrationStartDate: '', stages: [] });
      fetchEvents();
      alert('✅ Event created!');
    } catch (err) {
      alert(err.response?.data?.error || 'Error creating event');
    }
  };

  const handleEditClick = (event) => {
    setEditingEvent(event._id);
    // ✅ Convert UTC from MongoDB back to local time for the input
    setEditForm({
      title: event.title,
      description: event.description,
      date: event.date?.split('T')[0],
      venue: event.venue,
      totalSeats: event.totalSeats,
      allowCancellation: event.allowCancellation,
      rules: event.rules || '',
      details: event.details || '',
      registrationDeadline: toLocal(event.registrationDeadline),
      registrationStartDate: toLocal(event.registrationStartDate),
      stages: event.stages || []
    });
  };

  const handleEditSave = async (eventId) => {
    try {
      // ✅ Convert local times to UTC before saving
      const payload = {
        ...editForm,
        registrationDeadline: toUTC(editForm.registrationDeadline),
        registrationStartDate: toUTC(editForm.registrationStartDate),
      };
      await axios.put(`${API_URL}/api/events/${eventId}`, payload);
      setEditingEvent(null);
      fetchEvents();
      alert('✅ Event updated!');
    } catch (err) {
      alert(err.response?.data?.error || 'Error updating event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await axios.delete(`${API_URL}/api/events/${eventId}`);
      fetchEvents();
      if (selectedEvent === eventId) setSelectedEvent('');
    } catch (err) {
      alert(err.response?.data?.error || 'Error deleting event');
    }
  };

  const handleToggleLock = async (eventId, currentLock) => {
    try {
      await axios.put(`${API_URL}/api/events/${eventId}`, { isLocked: !currentLock });
      fetchEvents();
    } catch (err) {
      alert('Error updating lock status');
    }
  };

  const loadRegistrations = (eventId) => {
    setSelectedEvent(eventId);
    axios.get(`${API_URL}/api/registrations/event/${eventId}`)
      .then(res => setRegistrations(res.data));
  };

  const handleCheckin = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/registrations/checkin`, { email: scanEmail, eventId: selectedEvent });
      setScanMsg({ type: 'success', text: res.data.message });
      loadRegistrations(selectedEvent);
    } catch (err) {
      setScanMsg({ type: 'error', text: err.response?.data?.error || 'Error' });
    }
  };

  const handleRemoveStudent = async (registrationId) => {
    if (!window.confirm('Remove this student?')) return;
    try {
      await axios.delete(`${API_URL}/api/registrations/admin/${registrationId}`);
      loadRegistrations(selectedEvent);
    } catch (err) {
      alert(err.response?.data?.error || 'Error removing student');
    }
  };

  const checkedIn = registrations.filter(r => r.checkedIn).length;

  const inputStyle = {
    background: inputBg, border: `1.5px solid ${inputBorder}`, color: text,
    padding: '12px', borderRadius: '10px', fontSize: '14px', width: '100%',
    marginBottom: '14px', fontFamily: 'inherit', outline: 'none'
  };

  const cardStyle = {
    background: card, borderRadius: '16px', padding: '25px', marginBottom: '25px',
    border: `1px solid ${border}`, boxShadow: darkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 2px 15px rgba(0,0,0,0.07)'
  };

  const tabStyle = (tab) => ({
    padding: '10px 20px', borderRadius: '10px', fontSize: '14px', fontWeight: '600',
    cursor: 'pointer', border: 'none',
    background: activeTab === tab ? 'linear-gradient(135deg, #e94560, #c62a47)' : (darkMode ? 'rgba(255,255,255,0.05)' : '#f0f0f0'),
    color: activeTab === tab ? '#fff' : subtext,
    transition: '0.2s'
  });

  return (
    <div style={{ background: bg, minHeight: '100vh', padding: '30px 20px' }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        input[type="date"]::-webkit-calendar-picker-indicator,
        input[type="datetime-local"]::-webkit-calendar-picker-indicator {
          filter: ${darkMode ? 'invert(1)' : 'none'}; cursor: pointer;
        }
      `}</style>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

        <div style={{ marginBottom: '30px' }}>
          <h1 style={{ color: text, fontSize: '28px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <BarChart2 color="#e94560" size={28} /> Admin Dashboard
          </h1>
          <p style={{ color: subtext, marginTop: '5px' }}>Manage events and registrations</p>
        </div>

        <div style={{ display: 'flex', gap: '10px', marginBottom: '25px', flexWrap: 'wrap' }}>
          <button style={tabStyle('create')} onClick={() => setActiveTab('create')}>➕ Create Event</button>
          <button style={tabStyle('manage')} onClick={() => setActiveTab('manage')}>📋 Manage Events</button>
          <button style={tabStyle('analytics')} onClick={() => setActiveTab('analytics')}>📊 Analytics</button>
        </div>

        {activeTab === 'create' && (
          <div style={{ ...cardStyle, animation: 'fadeIn 0.3s ease' }}>
            <h2 style={{ color: text, marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Plus color="#e94560" size={20} /> Create New Event
            </h2>
            <EventForm form={newEvent} onChange={setNewEvent} darkMode={darkMode} isEdit={false} />
            <button onClick={createEvent} style={{ background: 'linear-gradient(135deg, #e94560, #c62a47)', color: '#fff', width: '100%', padding: '14px', fontSize: '15px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <Plus size={18} /> Create Event
            </button>
          </div>
        )}

        {activeTab === 'manage' && (
          <div style={{ animation: 'fadeIn 0.3s ease' }}>
            {events.length === 0 ? (
              <div style={{ ...cardStyle, textAlign: 'center', padding: '50px' }}>
                <p style={{ color: subtext }}>No events yet. Create one first!</p>
              </div>
            ) : (
              events.map(event => (
                <div key={event._id} style={cardStyle}>
                  {editingEvent === event._id ? (
                    <div>
                      <h3 style={{ color: text, marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Edit2 size={16} color="#e94560" /> Editing: {event.title}
                      </h3>
                      <EventForm form={editForm} onChange={setEditForm} darkMode={darkMode} isEdit={true} />
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button onClick={() => handleEditSave(event._id)} style={{ background: 'linear-gradient(135deg, #4caf50, #388e3c)', color: '#fff', padding: '11px 20px', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '7px', flex: 1, justifyContent: 'center' }}>
                          <Save size={15} /> Save Changes
                        </button>
                        <button onClick={() => setEditingEvent(null)} style={{ background: darkMode ? 'rgba(255,255,255,0.08)' : '#f0f0f0', color: text, padding: '11px 20px', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '7px' }}>
                          <X size={15} /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '15px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                          <h3 style={{ color: text, fontSize: '17px', fontWeight: '700', margin: 0 }}>{event.title}</h3>
                          {event.isLocked && <span style={{ background: darkMode ? 'rgba(255,68,68,0.15)' : '#ffebee', color: '#ff4444', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' }}>🔒 LOCKED</span>}
                        </div>
                        <p style={{ color: subtext, fontSize: '13px', marginBottom: '10px' }}>{event.description}</p>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '5px', color: subtext, fontSize: '13px' }}>
                            <Calendar size={13} color="#e94560" /> {formatDate(event.date)}
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '5px', color: subtext, fontSize: '13px' }}>
                            <MapPin size={13} color="#e94560" /> {event.venue}
                          </span>
                          <span style={{ background: darkMode ? 'rgba(233,69,96,0.1)' : '#fff5f7', color: '#e94560', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>
                            {event.registeredCount}/{event.totalSeats} seats
                          </span>
                          {event.registrationDeadline && <Countdown deadline={event.registrationDeadline} />}
                          <span style={{ background: event.allowCancellation ? (darkMode ? 'rgba(76,175,80,0.1)' : '#e8f5e9') : (darkMode ? 'rgba(255,152,0,0.1)' : '#fff8f0'), color: event.allowCancellation ? '#4caf50' : '#ff9800', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>
                            {event.allowCancellation ? '✓ Cancellable' : '🔒 No Cancel'}
                          </span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button onClick={() => handleToggleLock(event._id, event.isLocked)} style={{ background: event.isLocked ? (darkMode ? 'rgba(76,175,80,0.12)' : '#e8f5e9') : (darkMode ? 'rgba(255,68,68,0.12)' : '#ffebee'), color: event.isLocked ? '#4caf50' : '#ff4444', padding: '9px 16px', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', border: `1px solid ${event.isLocked ? 'rgba(76,175,80,0.2)' : 'rgba(255,68,68,0.2)'}` }}>
                          {event.isLocked ? <><Unlock size={14} /> Unlock</> : <><Lock size={14} /> Lock</>}
                        </button>
                        <button onClick={() => handleEditClick(event)} style={{ background: darkMode ? 'rgba(255,255,255,0.08)' : '#f0f0f0', color: text, padding: '9px 16px', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                          <Edit2 size={14} /> Edit
                        </button>
                        <button onClick={() => handleDeleteEvent(event._id)} style={{ background: darkMode ? 'rgba(233,69,96,0.12)' : '#ffebee', color: '#e94560', padding: '9px 16px', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', border: '1px solid rgba(233,69,96,0.2)' }}>
                          <Trash2 size={14} /> Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'analytics' && (
          <div style={{ animation: 'fadeIn 0.3s ease' }}>
            <div style={cardStyle}>
              <h2 style={{ color: text, marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <BarChart2 color="#e94560" size={20} /> Event Analytics
              </h2>
              <select onChange={e => loadRegistrations(e.target.value)} style={{ ...inputStyle, marginBottom: '20px' }}>
                <option value="">-- Select Event --</option>
                {events.map(e => <option key={e._id} value={e._id}>{e.title}</option>)}
              </select>

              {selectedEvent && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', marginBottom: '25px' }}>
                    {[
                      { icon: <Users size={22} color="#e94560" />, value: registrations.length, label: 'Total Registered', bg: darkMode ? 'rgba(233,69,96,0.1)' : '#fff5f7', border: 'rgba(233,69,96,0.2)' },
                      { icon: <CheckCircle size={22} color="#4caf50" />, value: checkedIn, label: 'Checked In', bg: darkMode ? 'rgba(76,175,80,0.1)' : '#f1f8f1', border: 'rgba(76,175,80,0.2)' },
                      { icon: <Clock size={22} color="#ff9800" />, value: registrations.length - checkedIn, label: 'Pending', bg: darkMode ? 'rgba(255,152,0,0.1)' : '#fff8f0', border: 'rgba(255,152,0,0.2)' }
                    ].map((stat, i) => (
                      <div key={i} style={{ background: stat.bg, border: `1px solid ${stat.border}`, borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                        <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center' }}>{stat.icon}</div>
                        <h3 style={{ fontSize: '28px', fontWeight: '700', color: text }}>{stat.value}</h3>
                        <p style={{ color: subtext, fontSize: '13px', marginTop: '4px' }}>{stat.label}</p>
                      </div>
                    ))}
                  </div>

                  <div style={{ background: darkMode ? 'rgba(255,255,255,0.03)' : '#f8f8f8', borderRadius: '12px', padding: '20px', marginBottom: '25px' }}>
                    <h3 style={{ color: text, marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <UserCheck size={18} color="#e94560" /> Manual Check-in
                    </h3>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <input placeholder="Enter student email" value={scanEmail} onChange={e => setScanEmail(e.target.value)} style={{ ...inputStyle, marginBottom: 0 }} />
                      <button onClick={handleCheckin} style={{ background: 'linear-gradient(135deg, #e94560, #c62a47)', color: '#fff', padding: '12px 24px', borderRadius: '10px', whiteSpace: 'nowrap' }}>
                        Check In
                      </button>
                    </div>
                    {scanMsg && (
                      <div style={{ marginTop: '12px', padding: '10px 15px', borderRadius: '8px', background: scanMsg.type === 'success' ? (darkMode ? 'rgba(76,175,80,0.15)' : '#e8f5e9') : (darkMode ? 'rgba(233,69,96,0.15)' : '#ffebee'), color: scanMsg.type === 'success' ? '#4caf50' : '#e94560', fontSize: '14px' }}>
                        {scanMsg.text}
                      </div>
                    )}
                  </div>

                  <h3 style={{ color: text, marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Users size={18} color="#e94560" /> Registrations
                  </h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: darkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
                          {['Name', 'Email', 'Roll No', 'Status', 'Action'].map(h => (
                            <th key={h} style={{ padding: '12px 15px', textAlign: 'left', color: subtext, fontSize: '13px', fontWeight: '600' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {registrations.map(r => (
                          <tr key={r._id} style={{ borderBottom: `1px solid ${border}`, transition: '0.2s' }}
                            onMouseEnter={e => e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.03)' : '#fafafa'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            <td style={{ padding: '12px 15px', color: text, fontSize: '14px' }}>{r.name}</td>
                            <td style={{ padding: '12px 15px', color: subtext, fontSize: '13px' }}>{r.email}</td>
                            <td style={{ padding: '12px 15px', color: subtext, fontSize: '13px' }}>{r.rollNo || '-'}</td>
                            <td style={{ padding: '12px 15px' }}>
                              <span style={{ background: r.checkedIn ? (darkMode ? 'rgba(76,175,80,0.15)' : '#e8f5e9') : (darkMode ? 'rgba(255,152,0,0.15)' : '#fff8f0'), color: r.checkedIn ? '#4caf50' : '#ff9800', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>
                                {r.checkedIn ? '✓ Checked In' : '⏳ Pending'}
                              </span>
                            </td>
                            <td style={{ padding: '12px 15px' }}>
                              <button onClick={() => handleRemoveStudent(r._id)} style={{ background: darkMode ? 'rgba(233,69,96,0.15)' : '#ffebee', color: '#e94560', padding: '6px 12px', fontSize: '12px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <Trash2 size={13} /> Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {registrations.length === 0 && (
                      <p style={{ textAlign: 'center', color: subtext, padding: '30px', fontSize: '14px' }}>No registrations yet</p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;